/**
 * Remove duplicate lines — pure, deterministic line deduplication.
 * Safe to render server-side.
 */

export interface DedupeOptions {
  /** Compare lines case-insensitively. */
  caseInsensitive?: boolean;
  /** Trim each line before comparing (and in the output). */
  trimEach?: boolean;
  /** Drop blank/whitespace-only lines entirely. */
  removeEmpty?: boolean;
  /** Sort the result alphabetically (otherwise first-occurrence order). */
  sort?: boolean;
}

export interface DedupeResult {
  lines: string[];
  /** Total lines dropped (duplicates plus removed blanks). */
  removed: number;
}

export function dedupeLines(
  text: string,
  opts: DedupeOptions = {},
): DedupeResult {
  const raw = text.split("\n");
  const seen = new Set<string>();
  const out: string[] = [];

  for (const line of raw) {
    const value = opts.trimEach ? line.trim() : line;
    if (opts.removeEmpty && value.trim() === "") continue;
    const key = opts.caseInsensitive ? value.toLowerCase() : value;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }

  if (opts.sort) out.sort((a, b) => a.localeCompare(b));

  return { lines: out, removed: raw.length - out.length };
}
